import type { WithExperienceFragment } from "~/contentful"

export type ContentfulFit = "pad" | "fill" | "scale" | "crop" | "thumb"
export type ContentfulFocus =
  | "center"
  | "top"
  | "right"
  | "left"
  | "bottom"
  | "top_right"
  | "top_left"
  | "bottom_right"
  | "bottom_left"
  | "face"
  | "faces"
export type ContentfulFormat = "png" | "jpg" | "webp" | "avif"

export const contentfulImageUrl = (
  url: string,
  {
    width,
    height,
    fit,
    focus,
    format = "png",
    quality = 50
  }: {
    width?: number
    height?: number
    fit?: ContentfulFit
    focus?: ContentfulFocus
    format?: ContentfulFormat
    quality?: number
  }
) =>
  [
    format && `${url}?fm=${format}`,
    width && `w=${width}`,
    height && `h=${height}`,
    fit && `fit=${fit}`,
    focus && `f=${focus}`,
    quality && `q=${quality}`
  ]
    .filter(Boolean)
    .join("&")

/**
 * Normalizes a URL from Contentful, only accepting:
 * - Relative paths starting with `/` (internal routes)
 * - Absolute URLs starting with `https://` or `http://` (external links)
 *
 * Returns `undefined` for bare domains (e.g. `www.example.com`) or
 * unexpected schemes that the Link component would mishandle.
 */
export const normalizeContentfulUrl = (
  url: string | null | undefined
): string | undefined => {
  const trimmed = url?.trim()
  if (!trimmed) return undefined
  if (trimmed.startsWith("/") || /^https?:\/\//.test(trimmed)) {
    return trimmed
  }
  return undefined
}

export const getFeaturedExperiences = (experiences: WithExperienceFragment[]) =>
  experiences
    .filter(exp => (exp.activityTypeLinksCollection?.items?.length ?? 0) > 2)
    .sort(
      (a, b) =>
        (b.activityTypeLinksCollection?.items?.length ?? 0) -
        (a.activityTypeLinksCollection?.items?.length ?? 0)
    )

export const getOtherExperiences = (
  allExperiences: WithExperienceFragment[],
  featuredExperiences: WithExperienceFragment[]
) => {
  const featuredIds = new Set(featuredExperiences.map(exp => exp.sys.id))
  return allExperiences.filter(exp => !featuredIds.has(exp.sys.id))
}
